// Sending email from the app. Every message goes through the sendMail Cloud Function
// (functions/mail.js), which sends it from the company account connected in
// Settings › Email — so it shows up in that account's Sent folder.
//
//   openCompose({ templateId, vals, links, to, subject, body, attachments, customerId, docType, ... })
//       the compose window (mailCompose.js): templates, lookup, Preview, drafts, Undo
//   pagesToPdf(pageElements, filename)  printable document pages -> PDF attachment
//   sendMail(payload)                   send without the window ({ layout: { text, ... } } or html)
//   customerContact(customerId)         { email, firstName } on file for a customer

import { database, functions } from './initFirebase';
import { ref, get } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';

/* ---------- sending ---------- */

/** Send one message. Resolves { from, provider }; rejects with a readable Error. */
export async function sendMail(payload) {
  try {
    const res = await httpsCallable(functions, 'sendMail', { timeout: 120000 })(payload);
    return res.data;
  } catch (e) {
    const msg = String(e?.message || e).replace(/^FirebaseError:\s*/, '');
    throw new Error(msg && msg !== 'internal' ? msg : 'The email didn\'t send. Try again in a minute.');
  }
}

/* ---------- business profile + signature ---------- */

let profilePromise = null;
export function businessProfile() {
  profilePromise ||= get(ref(database, 'settings/businessProfile'))
    .then(s => s.val() || {})
    .catch(() => ({}));
  return profilePromise;
}

/** { email, firstName } on file for a customer ('' when unknown). */
export async function customerContact(customerId) {
  if (!customerId) return { email: '', firstName: '' };
  try {
    const c = (await get(ref(database, `customers/${customerId}`))).val() || {};
    return { email: String(c.Email || '').trim(), firstName: String(c.First_Name || '').trim() };
  } catch { return { email: '', firstName: '' }; }
}

/* ---------- PDF from printable pages ---------- */

const blobToBase64 = blob => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1]);
  r.onerror = reject;
  r.readAsDataURL(blob);
});

/**
 * Render the document's page elements (816 x 1056 CSS px each, US Letter) into one PDF.
 * The libraries are loaded on first use only.
 */
export async function pagesToPdf(pages, filename) {
  const [{ jsPDF }, { default: html2canvas }] = await Promise.all([import('jspdf'), import('html2canvas')]);
  const pdf = new jsPDF({ unit: 'pt', format: 'letter', compress: true });
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const list = pages.filter(p => p && p.offsetParent !== null);
  if (!list.length) throw new Error('The document is not open.');
  for (let i = 0; i < list.length; i++) {
    const canvas = await html2canvas(list[i], {
      scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false,
      // The page sits in a scrolled overlay: measure it on its own.
      scrollX: 0, scrollY: -window.scrollY,
      onclone: doc => doc.querySelectorAll('.no-print').forEach(n => { n.style.display = 'none'; }),
    });
    const img = canvas.toDataURL('image/jpeg', 0.85);
    // Fit the page width; a page that overflowed its sheet is shrunk to fit the height.
    let w = W;
    let h = canvas.height * (W / canvas.width);
    if (h > H) { w = w * (H / h); h = H; }
    if (i > 0) pdf.addPage();
    pdf.addImage(img, 'JPEG', (W - w) / 2, 0, w, h, undefined, 'FAST');
  }
  const blob = pdf.output('blob');
  return { filename, contentType: 'application/pdf', base64: await blobToBase64(blob), size: blob.size };
}

/* ---------- the compose window ---------- */

// The window lives in mailCompose.js; it's re-exported here so callers keep one import.
export { openCompose, forgetSender } from './mailCompose';
